// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase";

import Dashboard from "./components/Dashboard";
import Login from "./components/Login";
import Consent from "./components/Consent";
import ConsentGate from "./components/ConsentGate";

// ✅ Error boundary with “Reset App”
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  async handleReset() {
    try {
      try { await signOut(auth); } catch {}
      try { localStorage.clear(); } catch {}
      try { sessionStorage.clear(); } catch {}
      window.location.assign("/login");
    } catch {
      window.location.reload();
    }
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "red", marginBottom: 12 }}>Something went wrong.</h2>
          <div style={{ marginBottom: 16, fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
            {this.state.error?.message || String(this.state.error)}
          </div>
          <button
            onClick={() => this.handleReset()}
            style={{
              padding: "10px 16px",
              borderRadius: 8,
              border: "none",
              background: "#0f62fe",
              color: "white",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Reset App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ user, children }) {
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const off = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return off;
  }, []);

  if (loading) return <p>Loading...</p>;

  return (
    <Router>
      <ErrorBoundary>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/dashboard" replace />}
          />
          <Route
            path="/consent"
            element={
              <RequireAuth user={user}>
                <Consent />
              </RequireAuth>
            }
          />
          <Route
            path="/dashboard"
            element={
              <RequireAuth user={user}>
                <ConsentGate>
                  <Dashboard />
                </ConsentGate>
              </RequireAuth>
            }
          />
          <Route
            path="*"
            element={<Navigate to={user ? "/dashboard" : "/login"} replace />}
          />
        </Routes>
      </ErrorBoundary>
    </Router>
  );
}
